import React, { useEffect, useState, useMemo, useRef } from "react";
import { fetchEntries, fetchMatters, fetchUsersLite, fetchTags } from "../lib/api";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Download } from "lucide-react";
import { todayISO, ndaysAgoISO } from "../lib/dates";

export default function ExportPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [matters, setMatters] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskCodes, setTaskCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [selectedMatter, setSelectedMatter] = useState("all");
  const [selectedUserIds, setSelectedUserIds] = useState([]); // Changed to array for multi-select
  const [range, setRange] = useState("30");
  const [fromDate, setFromDate] = useState(ndaysAgoISO(29));
  const [toDate, setToDate] = useState(todayISO());
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    }
    
    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserDropdown]);

  useEffect(() => {
    if (isAdmin) {
      loadMatters();
      loadUsers();
      loadTaskCodes();
      loadEntries(); // Load entries on mount
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      loadEntries();
    }
  }, [fromDate, toDate]);

  async function loadMatters() {
    try {
      const data = await fetchMatters();
      setMatters(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadUsers() {
    try {
      const data = await fetchUsersLite();
      setUsers(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadTaskCodes() {
    try {
      const data = await fetchTags();
      setTaskCodes(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadEntries() {
    setLoading(true);
    try {
      const data = await fetchEntries({ from: fromDate, to: toDate });
      setEntries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function handleRangeChange(val) {
    setRange(val);
    if (val === 'custom') return;
    
    const to = todayISO();
    let from;
    
    if (val === 'all') {
      from = '1970-01-01';
    } else {
      const days = parseInt(val, 10);
      from = ndaysAgoISO(days - 1);
    }
    
    setFromDate(from);
    setToDate(to);
  }

  // Filter entries based on selections
  const filteredEntries = useMemo(() => {
    let result = [...entries];
    
    // Filter by matter
    if (selectedMatter && selectedMatter !== "all") {
      result = result.filter(e => e.projectId === selectedMatter);
    }
    
    // Filter by users (multiple selection)
    if (selectedUserIds.length > 0) {
      result = result.filter(e => selectedUserIds.includes(e.userId));
    }
    
    return result;
  }, [entries, selectedMatter, selectedUserIds]);

  // Create user map for display
  const userMap = useMemo(() => {
    const map = {};
    users.forEach(u => {
      map[u.id] = u.name || u.email;
    });
    return map;
  }, [users]);

  // Create matter map for display
  const matterMap = useMemo(() => {
    const map = {};
    matters.forEach(m => {
      map[m.id] = m.name;
    });
    return map;
  }, [matters]);

  // Create task code map for display
  const taskCodeMap = useMemo(() => {
    const map = {};
    taskCodes.forEach(t => {
      map[t.id] = t.name;
    });
    return map;
  }, [taskCodes]);

  function handleUserToggle(userId) {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  }

  function handleSelectAllUsers() {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.id));
    }
  }

  function exportToCSV() {
    const rows = [
      ['Date', 'User Name', 'User Email', 'Description', 'Matter', 'Task Code', 'Hours', 'Rate', 'Total']
    ];
    
    filteredEntries.forEach(entry => {
      const userObj = users.find(u => u.id === entry.userId);
      const userName = entry.userName || userObj?.name || '';
      const userEmail = entry.userEmail || userObj?.email || '';
      const matterName = matterMap[entry.projectId] || '';
      const hours = entry.decimalHours ?? (entry.durationTenths / 10);
      const rate = (userObj && userObj.rate != null) ? Number(userObj.rate) : null;
      const total = (rate != null) ? (hours * rate) : null;
      
      // Get task code names from tagIds array
      const taskCodeNames = (entry.tagIds || [])
        .map(tagId => taskCodeMap[tagId])
        .filter(Boolean)
        .join(', ');
      
      rows.push([
        entry.date,
        userName,
        userEmail,
        entry.description || '',
        matterName,
        taskCodeNames,
        hours.toFixed(1),
        rate != null ? rate.toFixed(2) : '',
        total != null ? total.toFixed(2) : ''
      ]);
    });
    
    const csvContent = rows.map(row => 
      row.map(cell => {
        const str = String(cell);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const filename = `time_entries_export_${todayISO()}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <p className="text-muted-foreground">This page is only accessible to admins and superadmins.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Export Time Entries</h1>
        <Button onClick={exportToCSV} disabled={filteredEntries.length === 0}>
          <Download className="size-4 mr-2" />
          Export CSV ({filteredEntries.length} entries)
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Date Range</label>
            <Select value={range} onValueChange={handleRangeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Inputs */}
          {range === 'custom' && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          {/* Matter Filter */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Matter</label>
            <Select value={selectedMatter} onValueChange={setSelectedMatter}>
              <SelectTrigger>
                <SelectValue placeholder="All matters" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All matters</SelectItem>
                {matters.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* User Filter - Multi-select with checkboxes */}
          <div className="relative" ref={userDropdownRef}>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Users</label>
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              <span className="truncate">
                {selectedUserIds.length === 0
                  ? 'All users'
                  : selectedUserIds.length === users.length
                  ? 'All users'
                  : `${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''} selected`}
              </span>
              <span className="ml-2">▼</span>
            </Button>
            
            {showUserDropdown && (
              <div className="absolute top-full mt-1 w-full bg-background border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto">
                <div className="p-2 border-b sticky top-0 bg-background">
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.length === users.length}
                      onChange={handleSelectAllUsers}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">
                      {selectedUserIds.length === users.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </label>
                </div>
                <div className="p-2">
                  {users.map(u => (
                    <label
                      key={u.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={() => handleUserToggle(u.id)}
                        className="rounded"
                      />
                      <span className="text-sm">{u.name || u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadEntries} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <span className="text-xs text-muted-foreground">
            Showing {filteredEntries.length} of {entries.length} total entries
          </span>
        </div>
      </div>

      {/* Results Table */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading entries...</div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No entries found matching the selected filters.
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="max-h-[600px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-muted z-10">
                <TableRow>
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead className="w-[160px]">User</TableHead>
                  <TableHead className="w-[200px]">Description</TableHead>
                  <TableHead className="w-[140px]">Matter</TableHead>
                  <TableHead className="w-[130px]">Task Code</TableHead>
                  <TableHead className="text-right w-[80px]">Hours</TableHead>
                  <TableHead className="text-right w-[90px]">Rate</TableHead>
                  <TableHead className="text-right w-[100px]">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => {
                  // Priority: userName from backend > name from userMap > userEmail from backend > email from userMap > userId
                  const userName = entry.userName || userMap[entry.userId] || entry.userEmail || entry.userId;
                  const matterName = matterMap[entry.projectId] || '—';
                  const hours = entry.decimalHours ?? (entry.durationTenths / 10);
                  const userObj = users.find(u => u.id === entry.userId);
                  const rate = (userObj && userObj.rate != null) ? Number(userObj.rate) : null;
                  const total = (rate != null) ? (hours * rate) : null;
                  
                  // Get task code names from tagIds array
                  const taskCodeNames = (entry.tagIds || [])
                    .map(tagId => taskCodeMap[tagId])
                    .filter(Boolean)
                    .join(', ');
                  
                  return (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">{entry.date}</TableCell>
                      <TableCell className="text-sm">{userName}</TableCell>
                      <TableCell className="text-sm">{entry.description || '—'}</TableCell>
                      <TableCell className="text-sm">{matterName}</TableCell>
                      <TableCell className="text-sm">{taskCodeNames || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{hours.toFixed(1)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{rate != null ? `$${rate.toFixed(2)}` : '—'}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{total != null ? `$${total.toFixed(2)}` : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {/* Summary footer */}
          <div className="border-t bg-muted/50 px-4 py-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Total Entries: {filteredEntries.length}</span>
              <span className="font-semibold">
                Total Hours: {filteredEntries.reduce((sum, e) => {
                  const h = e.decimalHours ?? (e.durationTenths / 10);
                  return sum + h;
                }, 0).toFixed(1)}h
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
